import React, { useCallback, useEffect, useState } from 'react';
import * as PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
import {
  Button, Col, Row, Spin, Table, Tag, Tooltip, Typography, DatePicker, Popover, Input, Icon, notification,
} from 'antd';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import routeLocations from '../../../utility/route-locations';
import { selectAllInspections, selectInspections } from '../../../state/domain/inspections/inspectionListReducer';
import { selectFilterLoadParameters, selectInspectionListPageDataRequest } from '../../../state/ui/inspections/inspectionListUiReducer';
import { selectSessionUser } from '../../../state/ui/application/sessionUiReducer';
import {
  filterListPageData,
  requestInspectionListPageData, setLoadingParameterValues, requestInspectionListCSV,
} from '../../../state/ui/inspections/inspectionListUiActions';
import './InspectionsList.css';
import {
  INSPECTION_LIST_TAG_COLOUR_ALL,
  INSPECTION_LIST_TAG_COLOUR_CHECK,
  INSPECTION_LIST_TAG_COLOUR_COMPLIANCE, INSPECTION_LIST_TAG_COLOUR_COSTS,
  INSPECTION_LIST_TAG_COLOUR_DEFECTS,
  INSPECTION_LIST_TAG_COLOUR_NO_DEFECTS,
  INSPECTION_LIST_TAG_COLOUR_MY,
  INSPECTION_LIST_TAG_COLOUR_NEW_DAMAGE,
  INSPECTION_LIST_TAG_COLOUR_OTHERS,
} from '../dashboard/dashboard-util';
import useIsMobileSize from '../../../utility/hooks/useIsMobileSize';
import {getDefaultLocale} from "../../../configWeb";
import {getDateOnlyFromISO, getLastWeekDateRange, getLocalTimeDisplay} from "../bynx/packageUtil";


const { Column } = Table;
const { Text } = Typography;
const { Title } = Typography;
const { RangePicker } = DatePicker;


// function filterCompliance(value, record) {
//   return record.complianceIndicator === value;
// }

// function filterCurrency(value, record) {
//   return record.currencyCode === value;
// }

function filterInspectors(value, record) {
  return record.inspectorName === value;
}

function filterLocations(value, record) {
  return record.locationName === value;
}

// SORTS
const sortText = (a, b) => {
  const aStr = a.toString();
  const bStr = b.toString();
  return aStr.localeCompare(bStr);
};

function sortByRef(a, b) {
  return a.inspectionCode - b.inspectionCode;
}

function sortByExRef(a, b) {
  const aStr = `${a.externalContextReference}`;
  const bStr = `${b.externalContextReference}`;
  return aStr.localeCompare(bStr);
}

function sortVehiclesByDesc(a, b) {
  const aStr = `${a.vehicleDescription}`;
  const bStr = `${b.vehicleDescription}`;
  return aStr.localeCompare(bStr);
}

function sortByDate(a, b) {
  return new Date(a.inspectionDateTime).getTime() - new Date(b.inspectionDateTime).getTime();
}

function sortBySubmittedDate(a, b) {
  return new Date(a.submittedLocalTime).getTime() - new Date(b.submittedLocalTime).getTime();
}

function sortRegNo(a, b) {
  const aStr = `${a.regNumber}`;
  const bStr = `${b.regNumber}`;
  return aStr.localeCompare(bStr);
}

function sortChecklistFaults(a, b) {
  return a.noChecklistFaults - b.noChecklistFaults;
}

function sortNewDamages(a, b) {
  return a.noNewDamages - b.noNewDamages;
}

function sortCosts(a, b) {
  return a.totalEstimatedRepairCost - b.totalEstimatedRepairCost;
}

// Row total at top and bottom
const RowTotal = ({ rows, filteredSize, total }) => {
  const intl = useIntl();
  let showSize = rows;
  if (filteredSize && filteredSize < rows) {
    showSize = filteredSize;
  }
  const oneRow = (total && total === 1) || (!total && showSize === 1);
  return (
    <div>
      {showSize === total || total == null
        ? <Text>{`${showSize} `}</Text>
        : <Text>{`${showSize}/${total} `}</Text>}
      <FormattedMessage
        id="inspectionListFooterRows"
        values={{ endS: oneRow ? '' : intl.formatMessage({ id: 'pluralLetter' }) }}
      />
    </div>
  );
};

RowTotal.propTypes = {
  rows: PropTypes.number.isRequired,
  filteredSize: PropTypes.number,
  total: PropTypes.number,
};

RowTotal.defaultProps = {
  filteredSize: null,
  total: null,
};


/**
 * The table containing all the inspections.
 * @param {object} props The react props
 */
function InspectionsTable({
  isLoading, inspections, filteredRowSize, setFilteredRowSize, setFilters, filteredReason, setFilteredReason, sessionUser,
}) {
  const isMobile = useIsMobileSize();
  const intl = useIntl();

  // // Currency display
  // const renderCurrency = (currency) => {
  //   if (currency === '(NA)') return intl.formatMessage({ id: 'inspectionListDataNA' });
  //   if (currency === '(MULTIPLE)') return intl.formatMessage({ id: 'inspectionListDataMultiple' });
  //   return currency;
  // };

  // FILTERS
  const reasonShowing = []; // use for row calculation if list opened with filter (overridden below otherwise)
  function filterReason(value, record) {
    if (record.reasonDescription === value) {
      if (reasonShowing.indexOf(record.inspectionCode) < 0) {
        reasonShowing.push(record.inspectionCode);
        setFilteredRowSize(reasonShowing.length);
      }
      return true;
    }
    return false;
  }


  // Locale adjust
  const userLocale = sessionUser && sessionUser.locale ? sessionUser.locale.replace('_', '-') : getDefaultLocale().locale;

  // ROW CONTROL
  const tableChanged = (pagination, filters, sorter, extra) => {
    if (extra && extra.currentDataSource) {
      setFilteredRowSize(extra.currentDataSource.length); // overrides reasonShowing above on filter as that only needed first time
    }
    setFilters(filters); // keep filters so can clear

    // Set dynamic reason filter (set on screen here, can also be passed in)
    setFilteredReason(filters.reasonDescription);
  };

  // TABLE SETTINGS AND COLUMNS
  return (
    <Table
      pagination={{
        defaultPageSize: 20,
        showTotal: () => <RowTotal rows={inspections.length} filteredSize={filteredRowSize} total={null} />,
      }}
      loading={isLoading}
      dataSource={inspections}
      rowKey={inspection => inspection.inspectionCode}
      onChange={tableChanged}
      style={{ overflowX: isMobile && 'scroll' }}
    >
      <Column
        sorter={sortByRef}
        defaultSortOrder="descend"
        title={<FormattedMessage id="inspectionListReference" />}
        dataIndex="inspectionCode"
        key="inspectionCode"
      />
      <Column
        sorter={sortByExRef}
        defaultSortOrder="descend"
        title={<FormattedMessage id="yourReference" />}
        dataIndex="externalContextReference"
        key="externalContextReference"
      />
      <Column
        title={<FormattedMessage id="inspectionListReason" />}
        dataIndex="reasonDescription"
        key="reasonDescription"
        filters={
          inspections
            ? [...new Set(inspections.map(row => row.reasonDescription))].sort((a, b) => sortText(a, b)).map(desc => ({ text: desc, value: desc }))
            : ''
        }
        filteredValue={filteredReason}
        onFilter={filterReason}
      />
      <Column
        sorter={sortByDate}
        title={`${intl.formatMessage({ id: 'inspectionListCreatedDate' })}`}
        dataIndex="localTime"
        key="localTime"
        align="center"
        render={(dateVal) => {
          const dateText = dateVal ? getDateOnlyFromISO(dateVal).toLocaleDateString(userLocale) : null;
          const ttText = dateVal ? (`${getLocalTimeDisplay(dateVal, userLocale)} ${intl.formatMessage({ id: 'localTime' }).toLowerCase()}`) : '';
          return (<Tooltip title={ttText} overlayStyle={{maxWidth: '500px'}}>{dateText}</Tooltip>);
        }}
      />
      <Column
          sorter={sortBySubmittedDate}
          title={`${intl.formatMessage({ id: 'inspectionListSubmittedDate' })}`}
          dataIndex="submittedLocalTime"
          key="submittedLocalTime"
          align="center"
          render={(dateVal) => {
            const dateText = dateVal ? getDateOnlyFromISO(dateVal).toLocaleDateString(userLocale) : null;
            const ttText = dateVal ? (`${getLocalTimeDisplay(dateVal, userLocale)} ${intl.formatMessage({ id: 'localTime' }).toLowerCase()}`) : '';
            return (<Tooltip title={ttText} overlayStyle={{maxWidth: '500px'}}>{dateText}</Tooltip>);
          }}
      />
      <Column
        sorter={sortRegNo}
        title={<FormattedMessage id="inspectionListRegNo" />}
        dataIndex="regNumber"
        key="regNumber"
        {...getColumnSearchProps('regNumber', intl.formatMessage({ id: 'regNumber' }), intl)}
      />
      <Column
        sorter={sortVehiclesByDesc}
        title={<FormattedMessage id="inspectionListVehicleDesc" />}
        dataIndex="vehicleDescription"
        key="vehicleDescription"
      />
      <Column
        sorter={sortChecklistFaults}
        title={<FormattedMessage id="inspectionListChecklistFaults" />}
        dataIndex="noChecklistFaults"
        key="noChecklistFaults"
        align="center"
      />
      <Column
        sorter={sortNewDamages}
        title={<FormattedMessage id="inspectionListNewDamage" />}
        dataIndex="noNewDamages"
        key="noNewDamages"
        align="center"
      />
      <Column
        sorter={sortCosts}
        title={<FormattedMessage id="inspectionListEstimated" />}
        key="totalEstimatedRepairCost"
        align="right"
        render={row => (row.currencyCode === '(MULTIPLE)' ? intl.formatMessage({ id: 'seeDetails' }) : row.totalEstimatedRepairCost)}
      />
      <Column
        title={<FormattedMessage id="inspectionListInspector" />}
        dataIndex="inspectorName"
        key="inspectorName"
        filters={
          inspections
            ? [...new Set(inspections.map(row => row.inspectorName))].sort((a, b) => sortText(a, b)).map(desc => ({ text: desc, value: desc }))
            : ''
        }
        filteredValue={inspections.inspectorName}
        onFilter={filterInspectors}
      />
      <Column
          title={<FormattedMessage id="location" />}
          dataIndex="locationName"
          key="locationName"
          align="center"
          filters={
            inspections
                // eslint-disable-next-line max-len
                ? [...new Set(inspections.map(row => row.locationName))].sort((a, b) => sortText(a, b)).map(desc => ({ text: desc, value: desc }))
                : ''
          }
          filteredValue={inspections.locationName}
          onFilter={filterLocations}
      />
      <Column
        className="via-action-column"
        align="right"
        width="70px"
        render={(text, record) => (
          <Link to={`${routeLocations.inspection}/${record.inspectionCode}`} target="_blank">
            <Button size="small" type="primary" icon="file-done">
              <FormattedMessage id="view" />
            </Button>
          </Link>
        )}
      />
    </Table>
  );
}

InspectionsTable.propTypes = {
  isLoading: PropTypes.bool,
  inspections: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  filteredRowSize: PropTypes.number,
  setFilteredRowSize: PropTypes.func,
  setFilters: PropTypes.func,
  sessionUser: PropTypes.shape({ userId: PropTypes.number.isRequired }).isRequired,
};

InspectionsTable.defaultProps = {
  isLoading: false,
  inspections: [],
  filteredRowSize: null,
  setFilteredRowSize: () => {},
  setFilters: () => {},
};

function getColumnSearchProps(dataIndex, searchBoxText, intl) {
  let searchInput = () => {};
  const searchBoxBackGroundText = `${intl.formatMessage({ id: 'search' })} ${searchBoxText}`;

  return {
    filterDropdown: ({
   setSelectedKeys, selectedKeys, confirm, clearFilters,
 }) => (
        <div style={{ padding: 8 }}>
          <Input
              ref={(node) => { searchInput = node; }}
              placeholder={searchBoxBackGroundText}
              value={selectedKeys[0]}
              onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
              onPressEnter={confirm}
              style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
              type="primary"
              onClick={confirm}
              icon="search"
              size="small"
              style={{ width: 90, marginRight: 8 }}
          >
            {intl.formatMessage({ id: 'search' })}
          </Button>
          <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
    ),
    filterIcon: filtered => <Icon type="search" style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilter: (value, record) => record[dataIndex].toString().toLowerCase().replace(/\s/g, '').includes(value.toLowerCase().replace(/\s/g, '')),
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.select());
      }
    },
  };
}

const DownloadButton = ({ downloadReport }) => {
  const intl = useIntl();
  const [filename, setFilename] = useState();
  const [visible, setVisible] = useState();

  const downloadNotification = () => {
    const isChrome = window && !!window.chrome;
    const isEdge = window && window.navigator && window.navigator.userAgent && window.navigator.userAgent.indexOf('Edg') > -1;
    notification.info({
      message: intl.formatMessage({ id: 'inspections' }),
      description: intl.formatMessage({ id: 'downloadDescMsg' }),
      placement: isChrome && !isEdge ? 'bottomLeft' : 'topRight',
      duration: 3,
    });
  };

  const download = () => {
    if (filename && filename.length > 1) downloadReport(filename);
    else downloadReport(intl.formatMessage({id: 'inspections'}));
    setVisible(false);
    downloadNotification();
  };

  return (
    <Popover
      placement="bottomRight"
      title={intl.formatMessage({ id: 'enterFileName' })}
      visible={visible}
      content={(
        <Row>
          <Row type="flex" justify="end" align="bottom" style={{ marginBottom: '1em' }}>
            <Col span={20}>
              <Input value={filename} onPressEnter={() => download()} onChange={(e) => setFilename(e.target.value)} placeholder={intl.formatMessage({ id: 'enter' })} />
            </Col>
            <Col span={4}>
              .xlsx
            </Col>
          </Row>
          <Row type="flex" justify="end">
            <Button
              type="ghost"
              style={{ margin: '0.5em' }}
              onClick={() => setVisible(false)}
            >
              <FormattedMessage id="cancel" />
            </Button>
            <Button
              type="primary"
              icon="download"
              onClick={() => download()}
              style={{ margin: '0.5em' }}
            >
              <FormattedMessage id="downloadList" />
            </Button>
          </Row>
        </Row>
      )}
      trigger="click"
    >
      <Button
        size="small"
        type="primary"
        icon="download"
        style={{ margin: '0.5em' }}
        onClick={() => setVisible(true)}
      >
        <FormattedMessage id="downloadList" />
      </Button>
    </Popover>
  );
};

DownloadButton.propTypes = {
  downloadReport: PropTypes.func.isRequired,
};

function Inspections({
  match, isLoading, loadPageData, filterPageData, setLoadFilters, loadParams, inspections, allInspections, sessionUser, downloadCurrentReport,
}) {
  const isMobile = useIsMobileSize();

  // Reason code can be passed in
  const reasonDesc = match && match.params ? match.params.reason : null;

  // Track filtered rows
  const [filteredRowSize, setFilteredRowSize] = useState(null);
  const [filters, setFilters] = useState(null);
  const [tableFilterReason, setTableFilterReason] = useState(reasonDesc);

  // Default dates
  const getDefaultDates = () => {
    return getLastWeekDateRange();
  };

  // Category Tags
  const [allTagColour, setAllTagColour] = useState(INSPECTION_LIST_TAG_COLOUR_ALL);
  const [myTagColour, setMyTagColour] = useState(null);
  const [complianceTagColour, setComplianceTagColour] = useState(null);
  const [othersTagColour, setOthersTagColour] = useState(null);
  const [defectsTagColour, setDefectsTagColour] = useState(null);
  const [noDefectsTagColour, setNoDefectsTagColour] = useState(null);
  const [checklistTagColour, setChecklistTagColour] = useState(null);
  const [newDamageTagColour, setNewDamageTagColour] = useState(null);
  const [oldDamageTagColour, setOldDamageTagColour] = useState(null);
  const [costsTagColour, setCostsTagColour] = useState(null);
  const [dateValue, setDateValue] = useState(getDefaultDates());

  // Dynamic reasons filter (can be passed in, only set once)
  const [filteredReason, setFilteredReason] = useState([]);
  useEffect(() => {
    setFilteredReason(tableFilterReason ? [tableFilterReason] : []);
  }, [tableFilterReason]);

  const intl = useIntl();

  // Reset flags
  const resetTags = useCallback(() => {
    setAllTagColour(INSPECTION_LIST_TAG_COLOUR_ALL);
    setMyTagColour(null);
    setComplianceTagColour(null);
    setOthersTagColour(null);
    setDefectsTagColour(null);
    setNoDefectsTagColour(null);
    setChecklistTagColour(null);
    setNewDamageTagColour(null);
    setOldDamageTagColour(null);
    setCostsTagColour(null);
    setDateValue(getDefaultDates());

    // Clear filters
    if (filters) {
      Object.keys(filters).forEach((key) => {
        const filter = filters[key];
        if (filter && filter.length > 0) {
          filter.splice(0, filter.length);
        }
      });
    }

    // Clear filters and reset total
    setTableFilterReason(null);
    setFilteredReason([]);
    setFilteredRowSize(null);
  }, [filters, setTableFilterReason]);

  const downloadReport = useCallback((filename) => {
    const fromDate = loadParams.dates && loadParams.dates[0] ? moment.utc(loadParams.dates[0]).toISOString() : null;
    const toDate = loadParams.dates && loadParams.dates[1] ? moment.utc(loadParams.dates[1]).toISOString() : null;
    const downloadQuery = {
      inspections,
      filters: {
        ...filters,
        ...loadParams,
        allInspections: !loadParams.my,
        reasonDescription: filteredReason,
        inspectorName: (loadParams.my ? [sessionUser.displayName] : filters && filters.inspectorName),
        locationName: filters && filters.locationName,
        toDate,
        fromDate,
      },
    };
    delete downloadQuery.filters.my;
    delete downloadQuery.filters.dates;
    delete downloadQuery.filters.all;
    downloadCurrentReport(downloadQuery, filename);
  }, [filters, sessionUser, loadParams, downloadCurrentReport, inspections, filteredReason]);

  const clearDates = useCallback(() => {
    setDateValue(null);
    setFilteredRowSize(null);
    setLoadFilters({ ...loadParams, dates: null });
  }, [setDateValue, setLoadFilters, loadParams]);

  // Tag clicked on
  const handleTag = useCallback((colourValue, colourVar, colourFunction, reset = false) => {
    if (reset) {
      setFilteredRowSize(null);
    }

    // Colour of tag being changed
    const turningOn = !colourVar;
    const newColour = colourValue;

    // Filter parameters
    const parameters = {
      allTagColour,
      myTagColour,
      complianceTagColour,
      othersTagColour,
      defectsTagColour,
      noDefectsTagColour,
      checklistTagColour,
      newDamageTagColour,
      oldDamageTagColour,
      costsTagColour,
      reset,
    };
    const loadFilters = {
      all: allTagColour != null,
      my: myTagColour != null,
      compliance: complianceTagColour != null,
      other: othersTagColour != null,
      defects: defectsTagColour != null,
      noDefects: noDefectsTagColour != null,
      faults: checklistTagColour != null,
      damages: newDamageTagColour != null,
      costs: costsTagColour != null,
      dates: dateValue,
    };

    // Tag Control
    const tagControl = (resetAll) => {
      if (resetAll) {
        resetTags();
        loadFilters.all = true;
        loadFilters.my = false;
        loadFilters.compliance = false;
        loadFilters.other = false;
        loadFilters.defects = false;
        loadFilters.noDefects = false;
        loadFilters.faults = false;
        loadFilters.damages = false;
        loadFilters.costs = false;
        loadFilters.dates = getDefaultDates();
      } else {
        // Either All or My
        if (newColour === INSPECTION_LIST_TAG_COLOUR_ALL) {
          setMyTagColour(null);
          loadFilters.all = true;
          loadFilters.my = false;
        }
        if (newColour === INSPECTION_LIST_TAG_COLOUR_MY) {
          setAllTagColour(null);
          loadFilters.my = true;
          loadFilters.all = false;
        }

        // In Sync (turn other off if this one coming on)
        if (newColour === INSPECTION_LIST_TAG_COLOUR_COMPLIANCE) {
          loadFilters.compliance = turningOn;
          if (turningOn) {
            setOthersTagColour(null);
            loadFilters.other = false;
          }
        }
        if (newColour === INSPECTION_LIST_TAG_COLOUR_OTHERS) {
          loadFilters.other = turningOn;
          if (turningOn) {
            setComplianceTagColour(null);
            loadFilters.compliance = false;
          }
        }

        // Defects control
        if (newColour === INSPECTION_LIST_TAG_COLOUR_DEFECTS) {
          loadFilters.defects = turningOn;
          if (turningOn) {
            setNoDefectsTagColour(null);
            setChecklistTagColour(null);
            setNewDamageTagColour(null);
            setCostsTagColour(null);
            loadFilters.noDefects = false;
            loadFilters.faults = false;
            loadFilters.damages = false;
            loadFilters.costs = false;
          }
        }
        if (newColour === INSPECTION_LIST_TAG_COLOUR_NO_DEFECTS) {
          loadFilters.noDefects = turningOn;
          if (turningOn) {
            setDefectsTagColour(null);
            setChecklistTagColour(null);
            setNewDamageTagColour(null);
            setCostsTagColour(null);
            loadFilters.defects = false;
            loadFilters.faults = false;
            loadFilters.damages = false;
            loadFilters.costs = false;
          }
        }
        if (newColour === INSPECTION_LIST_TAG_COLOUR_CHECK) {
          loadFilters.faults = turningOn;
          if (turningOn) {
            setDefectsTagColour(null);
            setNoDefectsTagColour(null);
            loadFilters.defects = false;
            loadFilters.noDefects = false;
          }
        }
        if (newColour === INSPECTION_LIST_TAG_COLOUR_NEW_DAMAGE) {
          loadFilters.damages = turningOn;
          if (turningOn) {
            setDefectsTagColour(null);
            setNoDefectsTagColour(null);
            loadFilters.defects = false;
            loadFilters.noDefects = false;
          }
        }
        if (newColour === INSPECTION_LIST_TAG_COLOUR_COSTS) {
          loadFilters.costs = turningOn;
          if (turningOn) {
            setDefectsTagColour(null);
            setNoDefectsTagColour(null);
            loadFilters.defects = false;
            loadFilters.noDefects = false;
          }
        }
      }

      // Keep filters
      setLoadFilters(loadFilters);
    };

    // Call filter with new colour to show
    filterPageData(inspections, allInspections, {
      ...parameters, newColour, colourFunction, turningOn, userId: sessionUser.userId, tagControl, loadFilters,
    });
  }, [resetTags, allInspections, allTagColour, checklistTagColour, complianceTagColour,
    costsTagColour, defectsTagColour, noDefectsTagColour, filterPageData, inspections, myTagColour, setLoadFilters,
    newDamageTagColour, oldDamageTagColour, othersTagColour, dateValue, sessionUser.userId]);

  // MAIN LOAD (with filter if needed)
  useEffect(() => {
    loadPageData(loadParams, sessionUser.userId);
    if (loadParams && loadParams !== {}) {
      let myOn= false;
      Object.keys(loadParams).forEach((key) => {
        if (key === 'my') {
          myOn = loadParams[key];
          setMyTagColour(myOn ? INSPECTION_LIST_TAG_COLOUR_MY : null);
        }
        if (key === 'compliance') { setComplianceTagColour(loadParams[key] ? INSPECTION_LIST_TAG_COLOUR_COMPLIANCE : null); }
        if (key === 'other') { setOthersTagColour(loadParams[key] ? INSPECTION_LIST_TAG_COLOUR_OTHERS : null); }
        if (key === 'defects') { setDefectsTagColour(loadParams[key] ? INSPECTION_LIST_TAG_COLOUR_DEFECTS : null); }
        if (key === 'noDefects') { setNoDefectsTagColour(loadParams[key] ? INSPECTION_LIST_TAG_COLOUR_NO_DEFECTS : null); }
        if (key === 'faults') { setChecklistTagColour(loadParams[key] ? INSPECTION_LIST_TAG_COLOUR_CHECK : null); }
        if (key === 'damages') { setNewDamageTagColour(loadParams[key] ? INSPECTION_LIST_TAG_COLOUR_NEW_DAMAGE : null); }
        if (key === 'costs') { setCostsTagColour(loadParams[key] ? INSPECTION_LIST_TAG_COLOUR_COSTS : null); }
        if (key === 'dates') { setDateValue(loadParams[key]); }
      });
      if (myOn) {
        setAllTagColour(null);
      }
    }
  }, [loadPageData, loadParams, sessionUser.userId]);

  // MAIN COMPONENT
  return (
    <div className="via-inspection-list-width">
      <Row type="flex" justify="space-between" align="middle" style={{ margin: '1.5em 0.5em 0.1em 0.5em' }}>
        <Title level={2}><FormattedMessage id="inspectionListHeading" /></Title>
        <Row type="flex">
          <Button
            size="small"
            icon="reload"
            onClick={() => { loadPageData(loadParams, sessionUser.userId); }}
            style={{ margin: '0.5em' }}
          >
            <FormattedMessage id="inspectionListReload" />
          </Button>
          <DownloadButton downloadReport={downloadReport} />
        </Row>
      </Row>
      <Row type="flex" justify="space-between" align="middle" className="via-inspections-list-bar">
        <Col style={{ width: '10%', marginTop: '4.5em', display: isMobile && 'none' }}>
          <div className="via-button-inspection-list-circle-icon">
            <div className="via-button-inspection-list-circle-sizing">
              <Text style={{ marginLeft: '3em', marginTop: '1.2em' }} className="via-inspection-list-circle-text">
                <FormattedMessage id="inspectionListCategories" />
              </Text>
            </div>
          </div>
        </Col>
        <Col style={{ width: '90%', marginTop: '-0.4em' }}>
          <Row type="flex" align="middle" justify="space-between">
            <div>
              <Tag className="via-inspection-list-category-tag radio-left" color={allTagColour} onClick={() => handleTag(INSPECTION_LIST_TAG_COLOUR_ALL, allTagColour, setAllTagColour)}><FormattedMessage id="inspectionListAll" /></Tag>
              <Tag className="via-inspection-list-category-tag radio-right" color={myTagColour} onClick={() => handleTag(INSPECTION_LIST_TAG_COLOUR_MY, myTagColour, setMyTagColour)}><FormattedMessage id="inspectionListMy" /></Tag>
              <Tag className="via-inspection-list-category-tag" color={complianceTagColour} onClick={() => handleTag(INSPECTION_LIST_TAG_COLOUR_COMPLIANCE, complianceTagColour, setComplianceTagColour)}><FormattedMessage id="compliance" /></Tag>
              <Tag className="via-inspection-list-category-tag" color={othersTagColour} onClick={() => handleTag(INSPECTION_LIST_TAG_COLOUR_OTHERS, othersTagColour, setOthersTagColour)}><FormattedMessage id="others" /></Tag>
              <Tag className="via-inspection-list-category-tag" color={noDefectsTagColour} onClick={() => handleTag(INSPECTION_LIST_TAG_COLOUR_NO_DEFECTS, noDefectsTagColour, setNoDefectsTagColour)}><FormattedMessage id="inspectionListNoDefects" /></Tag>
              <Tag className="via-inspection-list-category-tag" color={defectsTagColour} onClick={() => handleTag(INSPECTION_LIST_TAG_COLOUR_DEFECTS, defectsTagColour, setDefectsTagColour)}><FormattedMessage id="inspectionListDefects" /></Tag>
              <Tag className="via-inspection-list-category-tag" color={checklistTagColour} onClick={() => handleTag(INSPECTION_LIST_TAG_COLOUR_CHECK, checklistTagColour, setChecklistTagColour)}><FormattedMessage id="inspectionListChecklistFaults" /></Tag>
              <Tag className="via-inspection-list-category-tag" color={newDamageTagColour} onClick={() => handleTag(INSPECTION_LIST_TAG_COLOUR_NEW_DAMAGE, newDamageTagColour, setNewDamageTagColour)}><FormattedMessage id="inspectionListNewDamage" /></Tag>
              {/* eslint-disable-next-line max-len */}
              {/* <Tag className="via-inspection-list-category-tag" color={oldDamageTagColour} onClick={() => handleTag(INSPECTION_LIST_TAG_COLOUR_OLD_DAMAGE, oldDamageTagColour, setOldDamageTagColour)}>{<FormattedMessage id="inspectionListExisting" />}</Tag> */}
              <Tag className="via-inspection-list-category-tag" color={costsTagColour} onClick={() => handleTag(INSPECTION_LIST_TAG_COLOUR_COSTS, costsTagColour, setCostsTagColour)}><FormattedMessage id="inspectionListEstimated" /></Tag>
            </div>
            <div>
              <Button type="link" icon="sync" onClick={() => handleTag(null, null, null, true)}><FormattedMessage id="reset" /></Button>
            </div>
          </Row>
          <Row type="flex" align="bottom" justify="space-between" style={{ marginTop: '0.5em', marginLeft: '0.2em' }}>
            <div>
              <Text style={{ marginRight: '0.5em', marginBottom: '0.5em', fontSize: '10pt' }}><FormattedMessage id="submittedBetween" /></Text>
              <RangePicker
                  size="small"
                  className={`via-inspection-list-date-picker ${dateValue ? 'chosen' : 'not-chosen'}`}
                  separator=">"
                  placeholder={[intl.formatMessage({ id: 'from' }), intl.formatMessage({ id: 'to' })]}
                  value={dateValue ? dateValue.map(val => moment(val)) : null}
                  allowClear={false}
                  onChange={(moments) => {
                    const datesArg = moments && moments.length > 1 ? [moments[0].valueOf(), moments[1].valueOf()] : null;
                    setLoadFilters({ ...loadParams, dates: datesArg });
                  }}
              />
              <Button type="link" style={{ left: '-0.5em' }} onClick={() => clearDates()}><FormattedMessage id="clearDates" /></Button>
            </div>
            <span style={{
              marginRight: '0.5em', fontSize: '10pt', fontWeight: '600', color: 'var(--via-dashboard-heading-colour)',
            }}
            >
              { isLoading
                ? <Spin />
                : <RowTotal rows={inspections.length} filteredSize={filteredRowSize} total={allInspections.length} />}
            </span>
          </Row>
        </Col>
      </Row>
      <Row>
        <InspectionsTable
          isLoading={isLoading}
          inspections={inspections}
          sessionUser={sessionUser}
          filteredRowSize={filteredRowSize}
          setFilteredRowSize={setFilteredRowSize}
          setFilters={setFilters}
          filteredReason={filteredReason}
          setFilteredReason={setFilteredReason}
        />
      </Row>
    </div>
  );
}

// Validate the prop type parameters.
Inspections.propTypes = {
  match: PropTypes.shape({ params: PropTypes.shape({ reason: PropTypes.string }) }),
  isLoading: PropTypes.bool,
  loadPageData: PropTypes.func.isRequired,
  filterPageData: PropTypes.func.isRequired,
  setLoadFilters: PropTypes.func.isRequired,
  loadParams: PropTypes.oneOfType([PropTypes.object]).isRequired,
  inspections: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  allInspections: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  sessionUser: PropTypes.shape({ userId: PropTypes.number.isRequired, displayName: PropTypes.string.isRequired }).isRequired,
  downloadCurrentReport: PropTypes.func.isRequired,
};

Inspections.defaultProps = {
  match: null,
  isLoading: false,
  inspections: [],
  allInspections: [],
};

/**
 * A function for connecting redux state to the component props.
 */
const mapStateToProps = (state) => {
  const pageDataRequest = selectInspectionListPageDataRequest(state);
  const loadParams = selectFilterLoadParameters(state);
  const inspections = selectInspections(state);
  const inspectionsAll = selectAllInspections(state);

  return {
    isLoading: pageDataRequest ? pageDataRequest.isLoading : false,
    error: pageDataRequest ? pageDataRequest.error : null,
    loadParams,
    inspections,
    allInspections: inspectionsAll,
    sessionUser: selectSessionUser(state),
  };
};

/**
 * A function for connecting redux action to the component props.
 */
const mapDispatchToProps = dispatch => ({
  loadPageData: (params, user) => dispatch(requestInspectionListPageData(params, user)),
  filterPageData: (inspections, allInspections, filterParams) => dispatch(filterListPageData(inspections, allInspections, filterParams)),
  setLoadFilters: paramObj => dispatch(setLoadingParameterValues(paramObj)),
  downloadCurrentReport: (downloadQuery, filename) => dispatch(requestInspectionListCSV(downloadQuery, filename)),
});

/**
 * Connect the redux state to our component.
 */
export default connect(mapStateToProps, mapDispatchToProps)(Inspections);
